import { FC, useCallback } from 'react'
import { cn } from 'src/utils/style'
import { Spinner } from 'src/components/spinner'
import { SurveyQuestionOption } from 'src/services/survey'

type CheckboxFieldProps = {
  options: SurveyQuestionOption[]
  variant?: string

  value: any
  translate?: string | null
  loading?: boolean
  disabled?: boolean
  isMultiple?: boolean
  questionDisplay?: string | null
  layout?: string | null

  handleResponse: (value: any) => void
  handleSearch: (value: string) => void
}

const CheckboxField: FC<CheckboxFieldProps> = ({
  options,
  value,
  handleResponse,
  disabled,
  loading,
  isMultiple,
}) => {
  const isSelected = (optionValue: any) =>
    (!isMultiple && value === optionValue) || (isMultiple && value?.includes(optionValue))

  const handleToggle = useCallback(
    (optionValue: any) => {
      if (!isMultiple) {
        handleResponse(value === optionValue ? null : optionValue)
        return
      }

      const current = Array.isArray(value) ? value : []

      if (current.includes(optionValue)) {
        handleResponse(current.filter((v: any) => v !== optionValue))
      } else {
        handleResponse([...current, optionValue])
      }
    },
    [value, isMultiple, handleResponse],
  )

  if (loading) {
    return (
      <div className="flex justify-center">
        <Spinner size="sm" />
      </div>
    )
  }

  return (
    <div className="mx-auto flex flex-col gap-3" data-testid="checkbox-field">
      {options.map((option, index) => {
        const checked = isSelected(option.value)
        return (
          <label
            key={option.value}
            data-testid={`checkbox-option-${index}`}
            className={cn(
              'flex cursor-pointer items-center gap-3 rounded-lg border px-4 py-3 transition-all duration-150 ease-in-out',
              checked
                ? 'bg-accent/5 border-accent'
                : 'hover:border-accent/50 border-border bg-surface',
              disabled && 'cursor-not-allowed opacity-50',
            )}
          >
            <input
              type="checkbox"
              checked={!!checked}
              onChange={() => handleToggle(option.value)}
              disabled={disabled}
              data-testid={`checkbox-input-${index}`}
              className="h-4 w-4 cursor-pointer"
              style={{ accentColor: 'var(--primary)' }}
            />
            <span className="text-text-primary">{option.cleanDisplay}</span>
          </label>
        )
      })}
    </div>
  )
}

export default CheckboxField
